import { NodeState, GetNodeWithState, GenericNode } from '../types'

type NodeData = Record<string, unknown>
type InputNodeData = GenericNode<NodeData>
type OutputNodeData = GenericNode<GetNodeWithState<NodeData>>

export const createNodeTreeStateGenerator = <N extends InputNodeData>({
    getNodeChildren,
}: {
    getNodeChildren: (node: N) => N[]
}) => {
    type NodeWithState = OutputNodeData
    let previousNodeIds = new Set<string>()

    return function generateNodeTreeWithState({
        nodes,
        selectedNodeIds,
    }: {
        nodes: N[]
        selectedNodeIds: string[]
    }): NodeWithState[] {
        const currentNodeIds = new Set(selectedNodeIds)

        function traverse(node: N, forceState?: NodeState): NodeWithState {
            const nodeId = String(node.nodeId)
            const wasChecked = previousNodeIds.has(nodeId)
            const isCurrentlyChecked = currentNodeIds.has(nodeId)
            const isFreshlyUnchecked = wasChecked && !isCurrentlyChecked
            const isFreshlyChecked = !wasChecked && isCurrentlyChecked
            const children = getNodeChildren(node)

            // If we have a force state from parent or this node is freshly checked,
            // it should propagate down
            if (!forceState) {
                if (isFreshlyChecked) {
                    forceState = 'checked'
                } else if (isFreshlyUnchecked) {
                    forceState = 'unchecked'
                }
            }

            if (children.length === 0) {
                // Leaf node - use force state or selectedNodeIds state
                return {
                    ...node,
                    nodes: [],
                    state:
                        forceState ||
                        (isCurrentlyChecked ? 'checked' : 'unchecked'),
                }
            }

            // Process children with force state
            const updatedChildren = children.map((child) =>
                traverse(child, forceState)
            )

            // Determine parent state based on children
            const childStates = updatedChildren.map((child) => child.state)
            const allChecked = childStates.every((state) => state === 'checked')
            const allUnchecked = childStates.every(
                (state) => state === 'unchecked'
            )

            let state: NodeState
            if (isFreshlyUnchecked) {
                state = 'unchecked'
            } else if (forceState === 'checked' || isFreshlyChecked) {
                state = 'checked'
            } else if (allChecked) {
                state = 'checked'
            } else if (allUnchecked || forceState === 'unchecked') {
                state = 'unchecked'
            } else {
                state = 'indeterminate'
            }

            const updatedNode = {
                ...node,
                nodes: updatedChildren,
                state,
            }

            return updatedNode
        }

        const result = nodes.map((node) => traverse(node))
        previousNodeIds = currentNodeIds

        return result
    }
}
